import React from "react";
import { ArrowRight, Anchor, Sparkles } from "lucide-react";

export default function Hero() {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative overflow-hidden bg-[#2E2E2E] text-white pt-32 pb-24 lg:pt-40 lg:pb-32"
    >
      {/* Decorative grid lines */}
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            "linear-gradient(to right, #ffffff 1px, transparent 1px), linear-gradient(to bottom, #ffffff 1px, transparent 1px)",
          backgroundSize: "64px 64px",
        }}
      />

      {/* Yellow accent line */}
      <div className="absolute top-28 left-0 w-24 h-[3px] bg-[#FFF44F] hidden md:block" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
        {/* LEFT: Headline */}
        <div className="lg:col-span-7 relative z-10">
          <div className="hh-fade-up hh-eyebrow on-dark mb-7" data-testid="hero-eyebrow">
            <Sparkles className="w-3.5 h-3.5" />
            Aufmerksamkeit · Strategie · Wachstum
          </div>

          <h1
            data-testid="hero-headline"
            className="hh-headline hh-fade-up hh-delay-1 text-[2.85rem] sm:text-[3.6rem] lg:text-[5.2rem]"
          >
            Wir machen<br />
            Marken{" "}
            <span className="hh-highlight-strong">unübersehbar.</span>
          </h1>

          <p
            data-testid="hero-subline"
            className="hh-fade-up hh-delay-2 mt-7 max-w-xl font-body text-white/75 text-base sm:text-lg leading-relaxed"
          >
            Hype Hafen ist deine Social Media Agentur aus Hamburg: strategischer Content,
            durchdachte Kanalführung und konsequenter Fokus auf organisches Wachstum,
            Anfragen und nachhaltige Sichtbarkeit. Dein Hafen, wenn deine Marke endlich
            anlegen soll.
          </p>

          <div className="hh-fade-up hh-delay-3 mt-10 flex flex-wrap items-center gap-4">
            <button
              onClick={() => scrollTo("kontakt")}
              data-testid="hero-cta-button"
              className="hh-btn-primary"
            >
              Kostenloses Erstgespräch
              <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => scrollTo("leistungen")}
              data-testid="hero-secondary-button"
              className="hh-btn-ghost"
            >
              Unsere Leistungen
            </button>
          </div>

          <div className="hh-fade-up hh-delay-4 mt-12 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-white/55">
            <div className="flex items-center gap-2">
              <Anchor className="w-4 h-4 text-[#FFF44F]" />
              <span>Aus Hamburg für Hamburg</span>
            </div>
            <div className="hidden sm:flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-[#2185D0]" />
              <span>Antwortzeit &lt; 24h</span>
            </div>
          </div>
        </div>

        {/* RIGHT: Visual */}
        <div className="lg:col-span-5 relative min-h-[420px] lg:min-h-[560px]">
          {/* Big Ocean Blue circle (decorative) */}
          <div
            className="hh-hero-circle"
            style={{ right: "-160px", bottom: "-160px" }}
            aria-hidden
          />
          <div
            className="absolute rounded-full border-2 border-[#FFF44F]/40"
            style={{ width: 140, height: 140, top: "-30px", right: "60px" }}
            aria-hidden
          />

          {/* Harbor image card */}
          <div
            className="absolute inset-0 rounded-[28px] overflow-hidden border border-white/10 shadow-2xl"
            data-testid="hero-image-card"
          >
            <img
              src="https://images.unsplash.com/photo-1551353471-1975b91e445b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
              alt="Hamburger Hafen bei Sonnenuntergang mit Containerkranen"
              className="w-full h-full object-cover"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#2E2E2E]/70 via-transparent to-transparent" />

            {/* Floating stat badge */}
            <div className="absolute left-5 bottom-5 bg-[#2E2E2E]/90 backdrop-blur-md border border-white/10 rounded-2xl px-4 py-3 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#FFF44F] flex items-center justify-center">
                <Anchor className="w-5 h-5 text-[#2E2E2E]" />
              </div>
              <div className="leading-tight">
                <div className="font-display text-white text-sm">Hype Hafen</div>
                <div className="font-body text-white/60 text-[0.72rem]">Wo Aufmerksamkeit anlegt.</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom yellow ticker */}
      <div className="relative mt-20 lg:mt-28 border-y border-white/10 bg-[#2E2E2E]/40 overflow-hidden">
        <div className="hh-marquee-track py-5">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="flex items-center gap-16 pr-16">
              {["Content Creation", "Social Media", "Storytelling", "Branding", "Strategie", "Community", "Organic Growth"].map((t, idx) => (
                <div key={`${i}-${idx}`} className="flex items-center gap-4">
                  <span className="w-2 h-2 rounded-full bg-[#FFF44F]" />
                  <span className="font-display text-white/70 uppercase tracking-[0.22em] text-xs sm:text-sm">{t}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
